import { useOrganizationBaseDetailsQuery } from "@modules/organization/queries.generated"
import { WatchQueryFetchPolicy } from "apollo-client"
import {
  MachinePricingTier,
  PlatformLimitSummaryName,
} from "@modules/graphql/types"

/**
 * A hook providing a quick access to 'popular' properties of the workspace
 * Free free to extend it to your needs
 */

export type Options = {
  fetchPolicy?: WatchQueryFetchPolicy
  skip: boolean
}

export function useWorkspaceDetailsShortcuts(
  organizationId: string | null,
  options?: Options
) {
  const { fetchPolicy = `cache-only`, skip = false } = options || {}

  const { data, loading, error } = useOrganizationBaseDetailsQuery({
    variables: { id: organizationId },
    fetchPolicy: fetchPolicy,
    skip: skip,
  })

  return {
    loading,
    error,
    name: data?.organizationDetails?.name,
    concurrencyTotalLimit:
      data?.organizationDetails?.billing?.plan?.totalFeatures?.concurrency
        ?.quantity,
    isOnFreePlan:
      data?.organizationDetails?.billing?.plan?.baseFeatures?.tier ===
      MachinePricingTier.Free,
    isOnProfessionalPlan:
      data?.organizationDetails?.billing?.plan?.baseFeatures?.tier ===
      MachinePricingTier.Professional,
    isOnPaidPlan:
      data?.organizationDetails?.billing?.plan?.baseFeatures?.tier !==
      MachinePricingTier.Free,
    isOnEnterprisePlan:
      data?.organizationDetails?.billing?.plan?.baseFeatures?.tier ===
      MachinePricingTier.Enterprise,
    isOnAgencyPlan:
      data?.organizationDetails?.billing?.plan?.baseFeatures?.tier ===
      MachinePricingTier.Agency,
    planSitesLimit:
      data?.organizationDetails?.billing?.plan?.baseFeatures?.sites,
    isOnLegacyPlan:
      data?.organizationDetails?.billing?.plan?.baseFeatures?.tier !==
        MachinePricingTier.Free &&
      data?.organizationDetails?.billing?.plan?.baseFeatures?.tier !==
        MachinePricingTier.Professional &&
      data?.organizationDetails?.billing?.plan?.baseFeatures?.tier !==
        MachinePricingTier.Agency &&
      data?.organizationDetails?.billing?.plan?.baseFeatures?.tier !==
        MachinePricingTier.Enterprise,
    baseFeatures: data?.organizationDetails?.billing?.plan?.baseFeatures,
    baseFeaturesLimits: {
      [PlatformLimitSummaryName.Bandwidth]:
        data?.organizationDetails?.billing?.plan?.baseFeatures?.bandwidth
          ?.quantity,
      [PlatformLimitSummaryName.CustomDomains]:
        data?.organizationDetails?.billing?.plan?.baseFeatures?.domains,
      [PlatformLimitSummaryName.HostingIntegrations]:
        data?.organizationDetails?.billing?.plan?.baseFeatures
          ?.thirdPartyHostingIntegrations,
      [PlatformLimitSummaryName.Invocations]:
        data?.organizationDetails?.billing?.plan?.baseFeatures?.invocations
          ?.quantity,
      [PlatformLimitSummaryName.MonthlyBuildMinutes]:
        data?.organizationDetails?.billing?.plan?.baseFeatures
          ?.monthlyBuildMinutes?.quantity,
      [PlatformLimitSummaryName.Requests]:
        data?.organizationDetails?.billing?.plan?.baseFeatures?.requests
          ?.quantity,
      [PlatformLimitSummaryName.Sites]:
        data?.organizationDetails?.billing?.plan?.baseFeatures?.sites,
      [PlatformLimitSummaryName.TotalSiteRoutes]:
        data?.organizationDetails?.billing?.plan?.baseFeatures?.totalSiteRoutes
          ?.quantity,
      [PlatformLimitSummaryName.Users]:
        data?.organizationDetails?.billing?.plan?.baseFeatures?.users?.quantity,
      [PlatformLimitSummaryName.ImageCdnRequests]:
        data?.organizationDetails?.billing?.plan?.baseFeatures?.imageCdnRequests
          ?.quantity,
      // added only to silent TS, this metric is provided only for workspaces on Free plan and it's not defined through `billing.plan` so there is no corresponding property on `plan.baseFeatures`
      [PlatformLimitSummaryName.MonthlyBuilds]: null,
    },
    isBlocked: data?.organizationDetails?.blocked,
  }
}
